import {
  GridColDef,
  GridColumnGroupingModel,
  GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import {
  ConfirmationCheck,
  Divider,
  GKButton,
  GKClientDataGrid,
  GKSearchSelect,
  GKTextField,
  GKTypography,
  Grid2,
  NoData,
  RupeeIcon,
  Show,
  Typography,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { InvoiceList } from "interfaces/Revenue";
import DashboardLayout from "layouts/DashboardLayout";
import { map } from "lodash";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "store/store";
import { numberToCurrency } from "utils";
import axiosInstance from "utils/axiosInstance";
import { extraColumnsField, handleDownloadFile } from "utils/helpers";
import useDocumentTitle from "utils/useDocumentTitle";

const PartnerInvoices = () => {
  useDocumentTitle("Add Commission");

  const { partnerId } = useParams();
  const navigate = useNavigate();

  const partner = useAppSelector((state) =>
    state?.app?.partnerList.find((item) => +item.value === +partnerId),
  );

  const { data: commissionCardDetails, isFetching: commissionCardLoading } =
    useFetchQuery({
      key: ["COMMISSION_MANAGER_CARD_DETAILS", partnerId],
      route: "/api/revenue/revenue-commission-manager/cards/",
      requestBody: { partner: partnerId },
    });

  const { data, isFetching } = useFetchQuery({
    key: ["COMMISSION_MANAGER_LIST", partnerId],
    route: "/api/revenue/revenue-commission-manager/",
    requestBody: {
      filter: JSON.stringify({ partner: [partnerId], status: ["Pending"] }),
    },
  });

  const form = useForm({
    defaultValues: {
      invoices: [
        {
          id: "",
          paidManagementFeeCommission: "",
          paidPerformanceFeeCommission: "",
          paidAmount: "",
          status: "",
        },
      ],
    },
    values: {
      invoices: data?.data?.data,
    },
  });

  const handleDownloadInvoice = (
    event: React.MouseEvent<HTMLElement>,
    params: GridRenderCellParams<InvoiceList>,
  ) => {
    event.stopPropagation();
    handleDownloadFile({
      link: params?.row?.invoicePdf,
      name: params?.row?.invoiceNumber,
    });
  };

  const columns: GridColDef[] = [
    {
      ...extraColumnsField("Invoice Amount/No.", "invoiceAmount", 160, 1),
      type: "number",
      renderCell: (params) => (
        <Typography textAlign={"right"} fontSize={14}>
          {numberToCurrency(+params?.row?.invoiceAmount)}
          <Typography
            fontSize={12}
            fontWeight={500}
            color="var(--neutral-500)"
            sx={{ cursor: "pointer" }}
            onClick={(event) => handleDownloadInvoice(event, params)}
          >
            {params?.row?.invoiceNumber}
          </Typography>
        </Typography>
      ),
    },
    {
      ...extraColumnsField("Fees Amount (₹)", "managementFee", 140, 1),
      type: "number",
    },
    {
      ...extraColumnsField("Commission", "managementFeeCommission", 120, 1),
      type: "number",
    },
    {
      ...extraColumnsField("To be Paid", "text1", 170, 1),
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const { rowIndex } = params?.row;

        return (
          <Controller
            control={form.control}
            name={`invoices.${rowIndex}.paidManagementFeeCommission`}
            render={({ field }) => (
              <GKTextField
                {...field}
                size="small"
                slotProps={{ input: { endAdornment: <RupeeIcon /> } }}
              />
            )}
          />
        );
      },
    },
    {
      ...extraColumnsField("Fees Amount (₹).", "performanceFee", 140, 1),
      type: "number",
    },
    {
      ...extraColumnsField("Commission", "performanceFeeCommission", 120, 1),
      type: "number",
    },
    {
      ...extraColumnsField("To be Paid", "text2", 150, 1),
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const { rowIndex } = params?.row;

        return (
          <Controller
            control={form.control}
            name={`invoices.${rowIndex}.paidPerformanceFeeCommission`}
            render={({ field }) => (
              <GKTextField
                {...field}
                size="small"
                slotProps={{ input: { endAdornment: <RupeeIcon /> } }}
              />
            )}
          />
        );
      },
    },
    {
      ...extraColumnsField("Total Commission to be paid (₹)", "text3", 250, 1),
      type: "number",
      renderCell: (params) => {
        const { rowIndex } = params?.row;

        return (
          <Typography>
            {numberToCurrency(
              +form.watch(`invoices.${rowIndex}.paidManagementFeeCommission`) +
                +form.watch(
                  `invoices.${rowIndex}.paidPerformanceFeeCommission`,
                ),
            )}
          </Typography>
        );
      },
    },
    {
      ...extraColumnsField("Status", "status", 130, 1),
      renderCell: (params) => {
        const { rowIndex } = params?.row;

        return (
          <Controller
            control={form.control}
            name={`invoices.${rowIndex}.status`}
            render={({ field }) => (
              <GKSearchSelect
                {...field}
                size="small"
                options={[
                  { name: "Unpaid", value: "Pending" },
                  { name: "Paid", value: "Paid" },
                ]}
                getOptionLabel={(opt) => opt?.name || opt}
                onChange={(event, val) => {
                  field.onChange(val.value);
                }}
                value={
                  [
                    { name: "Unpaid", value: "Pending" },
                    { name: "Paid", value: "Paid" },
                  ].find((item) => item.value === field.value) || null
                }
              />
            )}
          />
        );
      },
    },
  ];

  const columnGroupingModel: GridColumnGroupingModel = [
    {
      groupId: "management_fees",
      headerName: "Management Fees",
      description: "",
      headerAlign: "center",
      freeReordering: true,
      children: [
        { ...extraColumnsField("Fees Amount (₹).", "managementFee", 170, 1) },
        {
          ...extraColumnsField(
            "Commission.",
            "managementFeeCommission",
            170,
            1,
          ),
        },
        { ...extraColumnsField("To be Paid.", "text1", 170, 1) },
      ],
    },
    {
      groupId: "performance_fee",
      headerName: "Performance Fees",
      description: "",
      headerAlign: "center",
      freeReordering: true,
      children: [
        { ...extraColumnsField("Fees Amount (₹).", "performanceFee", 170, 1) },
        {
          ...extraColumnsField(
            "Commission.",
            "performanceFeeCommission",
            170,
            1,
          ),
        },
        { ...extraColumnsField("To be Paid.", "text2", 170, 1) },
      ],
    },
  ];

  const handleCommissionPaid = useMutation({
    mutationKey: ["PAY_COMMISSION"],
    mutationFn: (data) =>
      axiosInstance.patch(
        `/api/revenue/revenue-commission-manager/bulk-update/`,
        data,
      ),
    onSuccess: () => navigate(-1),
  });

  return (
    <DashboardLayout title="Add Commission" disablePadding>
      <form
        onSubmit={form.handleSubmit((values) =>
          handleCommissionPaid.mutate(
            values.invoices?.map(
              (dataItem: {
                paidManagementFeeCommission: string | number;
                paidPerformanceFeeCommission: string | number;
              }) => ({
                ...dataItem,
                paidAmount:
                  (+dataItem.paidManagementFeeCommission || 0) +
                  (+dataItem.paidPerformanceFeeCommission || 0),
              }),
            ),
          ),
        )}
      >
        <Grid2 container spacing={2} py={3}>
          <Grid2
            container
            size={12}
            spacing={0}
            borderRadius={"8px"}
            border={"1px solid var(--neutral-100)"}
            mx={3}
          >
            <Grid2 size={2.4} p="12px 16px">
              <Typography
                fontSize={12}
                fontWeight={400}
                color="var(--neutral-700)"
              >
                Partner Name
              </Typography>
              <GKTypography fontSize={16} fontWeight={600} color="var(--black)">
                {partner.name || "-"}
              </GKTypography>
            </Grid2>
            <Grid2
              size={2.4}
              p="12px 16px"
              borderLeft={"1px solid var(--neutral-100)"}
            >
              <Typography
                fontSize={12}
                fontWeight={400}
                color="var(--neutral-700)"
              >
                Total Unpaid Commissions
              </Typography>
              <GKTypography
                fontSize={16}
                fontWeight={600}
                loading={commissionCardLoading}
              >
                {numberToCurrency(
                  commissionCardDetails?.data?.data
                    ?.totalUnpaidCommissionAmount,
                )}
              </GKTypography>
            </Grid2>
            <Grid2
              size={2.4}
              p="12px 16px"
              borderLeft={"1px solid var(--neutral-100)"}
            >
              <Typography
                fontSize={12}
                fontWeight={400}
                color="var(--neutral-700)"
              >
                Total Commission
              </Typography>
              <GKTypography
                fontSize={16}
                fontWeight={600}
                loading={commissionCardLoading}
              >
                {numberToCurrency(
                  commissionCardDetails?.data?.data?.totalCommissionAmount,
                )}
              </GKTypography>
            </Grid2>
            <Grid2
              size={2.4}
              p="12px 16px"
              borderLeft={"1px solid var(--neutral-100)"}
            >
              <Typography
                fontSize={12}
                fontWeight={400}
                color="var(--neutral-700)"
              >
                Management Fees
              </Typography>
              <GKTypography
                fontSize={16}
                fontWeight={600}
                color="var(--black)"
                loading={commissionCardLoading}
              >
                {numberToCurrency(
                  commissionCardDetails?.data?.data?.totalManagementFee,
                )}
              </GKTypography>
            </Grid2>
            <Grid2
              size={2.4}
              p="12px 16px"
              borderLeft={"1px solid var(--neutral-100)"}
            >
              <Typography
                fontSize={12}
                fontWeight={400}
                color="var(--neutral-700)"
              >
                Performance Fees
              </Typography>
              <GKTypography
                fontSize={16}
                fontWeight={600}
                color="var(--black)"
                loading={commissionCardLoading}
              >
                {numberToCurrency(
                  commissionCardDetails?.data?.data?.totalPerformanceFee,
                )}
              </GKTypography>
            </Grid2>
          </Grid2>
          <Show.When
            withBorder={false}
            isTrue={form.getValues("invoices")?.length !== 0}
            showNodata
            extraComponent={
              <Grid2
                size={12}
                maxHeight={"calc(100vh - 271px)"}
                minHeight={"calc(100vh - 271px)"}
                overflow={"auto"}
                px={3}
              >
                <NoData
                  customImg={<ConfirmationCheck />}
                  text="No Unpaid Commissions Found!"
                  subText="Looks like all commissions for this partner are already settled."
                />
              </Grid2>
            }
          >
            <Grid2
              size={12}
              maxHeight={"calc(100vh - 271px)"}
              minHeight={"calc(100vh - 271px)"}
              overflow={"auto"}
              px={3}
            >
              <GKClientDataGrid
                experimentalFeatures={{ columnGrouping: true }}
                loading={isFetching}
                columns={columns}
                rows={
                  map(form.getValues("invoices"), (item, index) => ({
                    ...item,
                    rowIndex: index,
                  })) || []
                }
                rowSelection={false}
                name="partnerCommissionInvoice"
                showHeaderGrid={false}
                columnGroupingModel={columnGroupingModel}
              />
            </Grid2>
            <Grid2 size={12}>
              <Divider />
            </Grid2>
            <Grid2
              size={12}
              display={"flex"}
              justifyContent={"flex-end"}
              px={3}
            >
              <GKButton
                variant="contained"
                type="submit"
                loading={handleCommissionPaid?.isLoading}
              >
                Save Changes
              </GKButton>
            </Grid2>
          </Show.When>
        </Grid2>
      </form>
    </DashboardLayout>
  );
};

export default PartnerInvoices;
